import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ArrowLeft,
  Car,
  EllipsisVertical,
  ExternalLink,
  Lock,
  RefreshCw,
  WifiOff,
  X,
} from 'lucide-react';
import { useAppSelector } from '@store/hooks';
import { encrypt } from '@src/utils';
import { updateXHeaderId } from '@utils/storage';
import { setIsShowRide } from '@store/slices/rides/ridesSlice';
import {
  useGetDriverChatMessagesQuery,
  useGetDriverConversationQuery,
  useLoadOlderDriverChatMessagesMutation,
  useMarkDriverChatReadMutation,
  useSendDriverChatMessageMutation,
} from '@store/rtk/driverChat';
import { selectDriverChat } from '@store/slices/driverChat/driverChatSlice';
import { DriverAvatar } from '@component/DriverChat/DriverAvatar/DriverAvatar';
import { ChatComposer } from '@component/DriverChat/ChatComposer/ChatComposer';
import { MessageList } from '@component/DriverChat/ChatThread/MessageList';
import { TripCard } from '@component/DriverChat/ChatThread/TripCard';
import { useLiveDriver } from '@component/DriverChat/hooks/useLiveDriver';
import { useOnlineStatus } from '@component/DriverChat/hooks/useOnlineStatus';
import { useLiveChatStatus } from '@component/DriverChat/hooks/useLiveChatStatus';
import { useDriverConversations } from '@component/DriverChat/hooks/useDriverConversations';
import {
  createClientMessageId,
  getStatusLabel,
  mergeChatSeed,
} from '@component/DriverChat/helpers';
import {
  ChatMessage,
  DriverChatMode,
  DriverChatSeed,
  DriverConversation,
} from '@component/DriverChat/types';

type ChatThreadProps = {
  conversationId: number;
  seed: DriverChatSeed;
  mode: DriverChatMode;
  titleId: string;
  onClose: () => void;
  onBack?: () => void;
  dataTestId: string;
};

export const ChatThread: FC<ChatThreadProps> = ({
  conversationId,
  seed,
  mode,
  titleId,
  onClose,
  onBack,
  dataTestId,
}) => {
  const dispatch = useDispatch();
  const { segmentId } = seed;
  const isOnline = useOnlineStatus();
  const { connection } = useAppSelector(selectDriverChat);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    data: thread,
    isLoading,
    isError,
    refetch,
  } = useGetDriverChatMessagesQuery(conversationId, {
    refetchOnMountOrArgChange: true,
  });
  const messages = useMemo(() => thread?.messages ?? [], [thread]);
  const [loadOlder, { isLoading: isLoadingOlder }] =
    useLoadOlderDriverChatMessagesMutation();
  const { data: detail } = useGetDriverConversationQuery(conversationId);
  const { conversations } = useDriverConversations(false, true);
  const conversation = conversations.find(
    (c) => c.conversationId === conversationId
  );
  const [sendMessage] = useSendDriverChatMessageMutation();
  const [markRead] = useMarkDriverChatReadMutation();

  const info = useMemo(
    () =>
      mergeChatSeed<DriverConversation | DriverChatSeed>(
        { ...(conversation ?? {}), ...(detail ?? {}) } as DriverChatSeed,
        seed
      ),
    [conversation, detail, seed]
  );
  // Same live ride status as everywhere else in the app (socketStatus).
  const { statusId, isChatOpen } = useLiveChatStatus(
    segmentId,
    info.statusId,
    detail?.conversationStatus ?? conversation?.conversationStatus
  );
  const isClosed = !isChatOpen;
  const isReconnecting = connection === 'reconnecting';
  const driverName = info.driverFirstName || 'Driver';
  // The SMS goes to the driver's current number (resolved server-side); show the
  // freshest one we have so the dispatcher can see where it is going.
  const { phone: driverPhone, image: driverImage } = useLiveDriver(segmentId, {
    phone: info.driverPhone,
    image: info.driverImage,
  });

  // Capture where "New messages" starts once, when the thread first loads.
  const [firstUnreadId, setFirstUnreadId] = useState<string | null>(null);
  const unreadCapturedRef = useRef(false);
  const unreadCount = conversation?.unreadCount ?? 0;

  // Read receipts are cursor based: the newest real (server-id) driver message.
  const markThreadRead = () => {
    const lastDriverId = messages
      .filter((m) => m.sender === 'DRIVER' && !Number.isNaN(Number(m.id)))
      .reduce((max, m) => Math.max(max, Number(m.id)), 0);
    if (lastDriverId) {
      markRead({ conversationId, lastReadMessageId: lastDriverId });
    }
  };

  useEffect(() => {
    if (unreadCapturedRef.current || isLoading || !conversation) return;
    unreadCapturedRef.current = true;
    if (unreadCount > 0) {
      const driverMessages = messages.filter((m) => m.sender === 'DRIVER');
      setFirstUnreadId(
        driverMessages[driverMessages.length - unreadCount]?.id ?? null
      );
      markThreadRead();
    }
  }, [isLoading, messages, unreadCount, conversation]);

  // Unread can still grow from a refetch after the thread opened.
  useEffect(() => {
    if (unreadCapturedRef.current && unreadCount > 0) markThreadRead();
  }, [unreadCount]);

  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isMenuOpen]);

  const loadEarlier = () => {
    const oldestId = messages
      .map((m) => Number(m.id))
      .filter((id) => !Number.isNaN(id))
      .reduce((min, id) => Math.min(min, id), Infinity);
    if (Number.isFinite(oldestId)) {
      loadOlder({ conversationId, beforeMessageId: oldestId });
    }
  };

  const send = (text: string) => {
    setFirstUnreadId(null);
    sendMessage({
      conversationId,
      text,
      clientMessageId: createClientMessageId(),
    });
  };

  const retry = (message: ChatMessage) =>
    sendMessage({
      conversationId,
      text: message.text,
      clientMessageId: message.clientMessageId ?? message.id,
    });

  const openRide = () => {
    if (info.rideId == null) return;
    setIsMenuOpen(false);
    dispatch(setIsShowRide(true));
    updateXHeaderId('ride', info.rideId);
    window.open(
      `/rides/information/${encrypt({
        ride: info.rideId,
        selectedSegmentId: segmentId,
      })}`,
      '_blank'
    );
  };

  const presence = isClosed
    ? 'offline'
    : isReconnecting || !isOnline
      ? 'reconnecting'
      : 'online';

  const renderStatusLine = () => {
    if (isClosed) return <span>Conversation closed</span>;
    if (!isOnline) return <span className="is-warning">Offline</span>;
    if (isReconnecting) return <span className="is-warning">Reconnecting…</span>;
    return <span className="is-live">Live</span>;
  };

  const renderBanner = () => {
    if (isClosed) {
      return (
        <div className="dc-banner is-closed" role="status">
          <Lock size={16} strokeWidth={2} aria-hidden="true" />
          This conversation is read-only. Ride status:{' '}
          {getStatusLabel(statusId).toLowerCase()}.
        </div>
      );
    }
    if (!isOnline) {
      return (
        <div className="dc-banner is-warning" role="status">
          <WifiOff size={16} strokeWidth={2} aria-hidden="true" />
          You're offline. Reconnect to the internet to send messages.
        </div>
      );
    }
    if (isReconnecting) {
      return (
        <div className="dc-banner is-warning" role="status">
          <RefreshCw
            size={16}
            strokeWidth={2}
            className="dc-spin"
            aria-hidden="true"
          />
          Reconnecting… New messages from the driver may be delayed.
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dc-thread" data-test-id={`${dataTestId}-thread`}>
      <div className="dc-thread__header">
        {onBack && (
          <button
            type="button"
            className="dc-icon-button"
            aria-label="Back to conversations"
            onClick={onBack}
          >
            <ArrowLeft size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
        )}
        <DriverAvatar
          name={driverName}
          image={driverImage}
          presence={presence}
          size={mode === 'panel' ? 'sm' : 'md'}
        />
        <div className="dc-thread__identity">
          <div className="dc-thread__name" id={titleId}>
            {driverName}
            <span className="dc-tag">Driver</span>
          </div>
          <div className="dc-thread__subline">
            {renderStatusLine()}
            {driverPhone && !isClosed && (
              <>
                <span aria-hidden="true">·</span>
                <span className="dc-mono" data-test-id={`${dataTestId}-driver-phone`}>
                  {driverPhone}
                </span>
              </>
            )}
            {(info.vehicleLabel || info.licensePlate) && (
              <>
                <span aria-hidden="true">·</span>
                <Car size={14} strokeWidth={1.75} aria-hidden="true" />
                <span className="dc-thread__vehicle">
                  {info.vehicleLabel}
                  {info.vehicleLabel && info.licensePlate ? ' · ' : ''}
                  {info.licensePlate && (
                    <span className="dc-mono">{info.licensePlate}</span>
                  )}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="dc-thread__actions">
          {info.rideId != null && (
            <div className="dc-menu" ref={menuRef}>
              <button
                type="button"
                className="dc-icon-button"
                aria-label="More options"
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                onClick={() => setIsMenuOpen((open) => !open)}
              >
                <EllipsisVertical size={18} strokeWidth={1.75} aria-hidden="true" />
              </button>
              {isMenuOpen && (
                <div className="dc-menu__list" role="menu">
                  <button type="button" role="menuitem" onClick={openRide}>
                    <ExternalLink size={15} strokeWidth={1.75} aria-hidden="true" />
                    Open ride details
                  </button>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className="dc-icon-button"
            aria-label="Close chat"
            onClick={onClose}
          >
            <X size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
      </div>

      <TripCard
        info={info}
        statusId={statusId}
        isClosed={isClosed}
        dataTestId={dataTestId}
      />
      {renderBanner()}

      <MessageList
        messages={messages}
        firstUnreadId={firstUnreadId}
        isLoading={isLoading}
        isError={isError}
        driverName={driverName}
        driverImage={driverImage}
        hasMore={!!thread?.hasMore}
        isLoadingOlder={isLoadingOlder}
        onLoadOlder={loadEarlier}
        onReload={refetch}
        onRetry={retry}
      />

      {isClosed ? (
        <div className="dc-thread__closed">
          <Lock size={14} strokeWidth={2} aria-hidden="true" />
          Conversation closed
        </div>
      ) : (
        <ChatComposer
          conversationId={conversationId}
          driverFirstName={driverName}
          disabled={!isOnline}
          disabledPlaceholder="Waiting for connection…"
          onSend={send}
          dataTestId={dataTestId}
        />
      )}
    </div>
  );
};
